import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Blinds, DoorOpen, Key, LockKeyhole, Phone, ShieldCheck, Vault } from "lucide-react";
import SEO from "../components/SEO";
import EditorialMarquee from "../components/EditorialMarquee";
import StatusBadge from "../components/StatusBadge";
import { FadeUp, MaskedLines, SectionLabel } from "../components/Reveal";
import { PHONE_DISPLAY, PHONE_TEL, SERVICES } from "../data/site";

const SERVICE_ICONS = [DoorOpen, LockKeyhole, ShieldCheck, Blinds, Vault, Key];

export default function Services() {
    return (
        <div data-testid="services-page">
            <SEO
                title="Nos Services de Serrurerie — Serrurier Paris & Île-de-France | 24/7"
                description="Services serrurerie Paris : ouverture de porte 60€ TTC, changement de serrure dès 85€, porte blindée, rideau métallique, coffre-fort, double de clé. Artisan agréé assurance, 24h/24. ☎ 06 25 25 86 19"
                path="/services"
            />

            <section className="grain relative overflow-hidden bg-midnight py-10 text-white md:py-16">
                <div className="pointer-events-none absolute -right-24 top-0 h-80 w-80 rounded-full bg-emergency/20 blur-3xl" />
                <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <nav className="mb-8 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/50" data-testid="breadcrumb">
                        <Link to="/" className="transition-colors hover:text-gold">Accueil</Link>
                        <span>/</span>
                        <span className="text-gold">Services</span>
                    </nav>
                    <SectionLabel dark>Dépannage serrurerie</SectionLabel>
                    <StatusBadge className="mt-6" />
                    <h1 className="mt-4 max-w-3xl font-display text-4xl font-extrabold leading-[1.02] tracking-tighter sm:text-5xl lg:text-6xl">
                        <MaskedLines delay={0.15} lines={["Nos services", "de serrurerie."]} />
                    </h1>
                    <FadeUp delay={0.55}>
                        <p className="mt-6 max-w-xl leading-relaxed text-white/70">
                            6 services de dépannage indispensables, du simple double de clé au blindage complet.
                            Devis gratuit avant chaque intervention, prix annoncé et respecté.
                        </p>
                    </FadeUp>
                </div>
            </section>

            <EditorialMarquee dark={false} />

            <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
                <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                    {SERVICES.map((s, i) => {
                        const Icon = SERVICE_ICONS[i];
                        return (
                            <FadeUp key={s.title} delay={(i % 3) * 0.08}>
                                <Link
                                    to={s.slug}
                                    data-testid={`services-card-${i}`}
                                    className="group flex h-full flex-col justify-between rounded-xl border border-midnight/10 bg-white p-8 shadow-[0_24px_60px_-28px_rgba(10,17,40,0.18)] transition-all duration-300 hover:-translate-y-2 hover:border-midnight hover:shadow-[0_28px_60px_-20px_rgba(10,17,40,0.35)]"
                                >
                                    <div>
                                        <div className="flex items-start justify-between">
                                            <span className="flex h-12 w-12 items-center justify-center bg-midnight/5 text-emergency transition-colors duration-300 group-hover:bg-emergency group-hover:text-white">
                                                <Icon className="h-6 w-6" strokeWidth={1.8} />
                                            </span>
                                            <span className="bg-midnight px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-gold">
                                                {s.price}
                                            </span>
                                        </div>
                                        <h2 className="mt-6 font-display text-2xl font-extrabold tracking-tight">{s.title}</h2>
                                        <p className="mt-3 text-sm leading-relaxed text-midnight/60">{s.desc}</p>
                                    </div>
                                    <span className="mt-6 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-emergency">
                                        Voir le service
                                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                                    </span>
                                </Link>
                            </FadeUp>
                        );
                    })}
                </div>

                <FadeUp delay={0.1}>
                    <div className="mt-12 flex flex-wrap items-center justify-between gap-6 rounded-xl border border-midnight/10 bg-white p-8 shadow-[0_24px_60px_-20px_rgba(10,17,40,0.28)]" data-testid="services-info">
                        <div className="max-w-xl">
                            <h2 className="font-display text-2xl font-extrabold tracking-tight">Un besoin qui ne rentre dans aucune case ?</h2>
                            <p className="mt-3 text-sm leading-relaxed text-midnight/60">
                                Dépannage de volet roulant, intervention après cambriolage, entretien annuel de
                                serrurerie : décrivez votre situation, nous établissons un devis gratuit.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <Link
                                to="/devis-contact"
                                data-testid="services-devis-button"
                                className="flex items-center gap-2 bg-midnight px-6 py-3.5 font-bold text-white transition-colors hover:bg-navy"
                            >
                                Devis gratuit <ArrowUpRight className="h-4 w-4" />
                            </Link>
                            <a
                                href={PHONE_TEL}
                                data-testid="services-call-button"
                                className="flex items-center gap-2 bg-emergency px-6 py-3.5 font-bold text-white transition-colors hover:bg-emergency-dark"
                            >
                                <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
                            </a>
                        </div>
                    </div>
                </FadeUp>
                <FadeUp delay={0.15}>
                    <div className="mt-16 grid gap-8 lg:grid-cols-2">
                        <div className="border-l-2 border-gold bg-cloud p-8">
                            <h2 className="font-display text-2xl font-extrabold tracking-tight">
                                Artisan serrurier agréé assurance depuis 1985
                            </h2>
                            <p className="mt-4 leading-relaxed text-midnight/70">
                                Chaque intervention est réalisée par un artisan qualifié, agréé par les principales compagnies d'assurance habitation. Vous recevez une facture détaillée après l'intervention, conforme aux exigences de votre assureur, ainsi que les certificats de garantie sur le matériel posé.
                            </p>
                            <p className="mt-3 leading-relaxed text-midnight/70">
                                Nos cylindres et serrures sont certifiés A2P (une, deux ou trois étoiles) selon vos besoins. Renseignez-vous auprès de votre assureur sur le niveau exigé par votre contrat.
                            </p>
                        </div>
                        <div className="bg-midnight p-8 text-white">
                            <h2 className="font-display text-2xl font-extrabold tracking-tight">
                                Questions sur nos services ?
                            </h2>
                            <div className="mt-6 divide-y divide-white/10">
                                {[
                                    { q: "Quel service pour une porte claquée ?", a: "L'ouverture de porte à 60€ TTC. Notre artisan utilise la technique radio pour les portes claquées — moins de 5 minutes sur place, sans casse." },
                                    { q: "Mon assurance couvre-t-elle le serrurier ?", a: "Oui, si vous avez une garantie « assistance et dépannage » ou « bris de glace et serrures » dans votre contrat habitation. Nous vous fournissons tous les justificatifs nécessaires au remboursement." },
                                    { q: "Puis-je avoir un devis avant l'intervention ?", a: "Absolument. Un devis gratuit est établi par téléphone pour toute demande. Pour les interventions complexes (blindage, rideau métallique), un devis sur place est réalisé sans engagement avant tout travail." },
                                ].map((item, i) => (
                                    <div key={i} className="py-4">
                                        <p className="font-bold text-white">{item.q}</p>
                                        <p className="mt-1 text-sm leading-relaxed text-white/65">{item.a}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </FadeUp>


            </section>
        </div>
    );
}
